import React from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Segmented,
  Skeleton,
  Statistic,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  AnalyticsMetricPointDto,
  AnalyticsRoomSeriesDto,
  AnalyticsUnit,
  PokerSiteId,
} from '@poker-stats/contracts';
import { pokerSitePostfix } from '../StatPage/utils';
import { loadAnalyticsOverview, loadAnalyticsRooms } from './reducer';
import {
  selectAnalyticsOverview,
  selectAnalyticsOverviewError,
  selectAnalyticsOverviewLoading,
  selectAnalyticsOverviewUnit,
  selectAnalyticsRooms,
  selectAnalyticsRoomsError,
  selectAnalyticsRoomsLoading,
  selectAnalyticsRoomsUnit,
} from './selectors';
import './AdminHomePage.css';

const UNIT_OPTIONS = [
  { label: 'admin_analytics_unit_day', value: 'day' },
  { label: 'admin_analytics_unit_week', value: 'week' },
  { label: 'admin_analytics_unit_month', value: 'month' },
  { label: 'admin_analytics_unit_year', value: 'year' },
] as const satisfies ReadonlyArray<{ label: string; value: AnalyticsUnit }>;

const ROOM_COLORS: Record<PokerSiteId, string> = {
  1: '#35b2f6',
  2: '#55c983',
  3: '#f0b95b',
  4: '#a77bf3',
  5: '#f17a8b',
  6: '#54d1c5',
};

const ROOM_COLORS_V2: Record<PokerSiteId, string> = {
  1: '#dcfa54',
  2: '#55c983',
  3: '#f0b95b',
  4: '#c4a0ff',
  5: '#ff9aab',
  6: '#54d1c5',
};

type AdminHomePageProps = {
  embedded?: boolean;
};

const CHART_TOOLTIP_LEGACY = { background: '#272e3b', border: '1px solid #475265' };
const CHART_TOOLTIP_V2 = { background: '#23261e', border: '1px solid #3f4339' };

type MetricKey =
  | 'averageDau'
  | 'averageWau'
  | 'averageMau'
  | 'dauMauPercent'
  | 'successfulSearches'
  | 'searchesPerActiveUserDay';

const METRICS = [
  { key: 'averageDau', title: 'admin_analytics_dau', hint: 'admin_analytics_dau_hint' },
  { key: 'averageWau', title: 'admin_analytics_wau', hint: 'admin_analytics_wau_hint' },
  { key: 'averageMau', title: 'admin_analytics_mau', hint: 'admin_analytics_mau_hint' },
  {
    key: 'dauMauPercent',
    title: 'admin_analytics_dau_mau',
    hint: 'admin_analytics_dau_mau_hint',
    percent: true,
  },
  {
    key: 'successfulSearches',
    title: 'admin_analytics_searches',
    hint: 'admin_analytics_searches_hint',
  },
  {
    key: 'searchesPerActiveUserDay',
    title: 'admin_analytics_searches_per_active',
    hint: 'admin_analytics_searches_per_active_hint',
  },
] as const satisfies ReadonlyArray<{
  key: MetricKey;
  title: string;
  hint: string;
  percent?: boolean;
}>;

function UnitFilter({
  value,
  onChange,
}: {
  value: AnalyticsUnit;
  onChange: (value: AnalyticsUnit) => void;
}) {
  const { t } = useTranslation('translation');
  return (
    <Segmented<AnalyticsUnit>
      block
      value={value}
      options={UNIT_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))}
      onChange={onChange}
    />
  );
}

function MetricCard({
  points,
  metric,
  embedded = false,
}: {
  points: AnalyticsMetricPointDto[];
  metric: (typeof METRICS)[number];
  embedded?: boolean;
}) {
  const { t, i18n } = useTranslation('translation');
  const current = points[points.length - 1];
  const previous = points[points.length - 2];
  const currentValue = current?.[metric.key] ?? null;
  const previousValue = previous?.[metric.key] ?? null;
  const delta =
    currentValue == null || previousValue == null || previousValue === 0
      ? null
      : ((currentValue - previousValue) / previousValue) * 100;
  const chartData = points.map((point) => ({
    label: `${point.label}${point.isPartial ? '*' : ''}`,
    value: point[metric.key],
  }));
  const formatter = React.useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }),
    [i18n.language],
  );

  return (
    <article className="SurfaceCard SurfaceCard--compact AnalyticsMetricCard">
      <div className="AnalyticsMetricCard__Header">
        <Typography.Text type="secondary">{t(metric.title)}</Typography.Text>
        <Tooltip title={t(metric.hint)}>
          <InfoCircleOutlined aria-label={t(metric.hint)} />
        </Tooltip>
      </div>
      <div className="AnalyticsMetricCard__ValueRow">
        <Typography.Title level={3} className="AnalyticsMetricCard__Value">
          {currentValue == null ? '—' : formatter.format(currentValue)}
          {'percent' in metric && metric.percent && currentValue != null ? '%' : ''}
        </Typography.Title>
        <Typography.Text
          className={
            delta == null
              ? 'AnalyticsMetricCard__Delta'
              : delta >= 0
                ? 'AnalyticsMetricCard__Delta AnalyticsMetricCard__Delta--up'
                : 'AnalyticsMetricCard__Delta AnalyticsMetricCard__Delta--down'
          }
        >
          {delta == null ? '—' : `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}%`}
        </Typography.Text>
      </div>
      <div className="AnalyticsMetricCard__Chart" aria-label={t(metric.title)}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <ChartTooltip
              formatter={(value: number) => [formatter.format(value), t(metric.title)]}
              contentStyle={embedded ? CHART_TOOLTIP_V2 : CHART_TOOLTIP_LEGACY}
            />
            <XAxis dataKey="label" axisLine={false} tickLine={false} fontSize={11} />
            <Line
              dataKey="value"
              type="monotone"
              stroke={embedded ? '#dcfa54' : '#35b2f6'}
              strokeWidth={2}
              dot={{ r: 3, fill: embedded ? '#dcfa54' : '#35b2f6' }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
}

const AdminHomePage = React.memo(({ embedded = false }: AdminHomePageProps) => {
  const { t, i18n } = useTranslation('translation');
  const dispatch = useDispatch();
  const overview = useSelector(selectAnalyticsOverview);
  const rooms = useSelector(selectAnalyticsRooms);
  const overviewUnit = useSelector(selectAnalyticsOverviewUnit);
  const roomsUnit = useSelector(selectAnalyticsRoomsUnit);
  const overviewLoading = useSelector(selectAnalyticsOverviewLoading);
  const roomsLoading = useSelector(selectAnalyticsRoomsLoading);
  const overviewError = useSelector(selectAnalyticsOverviewError);
  const roomsError = useSelector(selectAnalyticsRoomsError);
  const [selectedRooms, setSelectedRooms] = React.useState<PokerSiteId[]>([1, 2, 3, 4, 5, 6]);

  React.useEffect(() => {
    dispatch(loadAnalyticsOverview('day'));
    dispatch(loadAnalyticsRooms('day'));
  }, [dispatch]);

  const numberFormatter = React.useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }),
    [i18n.language],
  );

  const roomChartData = React.useMemo(
    () =>
      rooms?.periods.map((period, index) => {
        const point: Record<string, string | number> = {
          label: `${period.label}${period.isPartial ? '*' : ''}`,
        };
        for (const room of rooms.rooms) {
          point[String(room.pokersiteId)] = room.points[index]?.searches ?? 0;
        }
        return point;
      }) ?? [],
    [rooms],
  );

  const renderOverview = () => {
    if (overviewLoading && !overview) {
      return <Skeleton active paragraph={{ rows: 8 }} />;
    }
    if (!overview) {
      return <Empty description={t('admin_analytics_empty')} />;
    }
    return (
      <>
        {overview.dataAvailableFrom.searches && (
          <Alert
            type="info"
            showIcon
            message={t('admin_analytics_available_from', {
              date: overview.dataAvailableFrom.searches,
            })}
          />
        )}
        <div className="AnalyticsDashboard__MetricGrid">
          {METRICS.map((metric) => (
            <MetricCard
              key={metric.key}
              points={overview.points}
              metric={metric}
              embedded={embedded}
            />
          ))}
        </div>

        <section className="SurfaceCard AnalyticsDashboard__Registration">
          <div className="AnalyticsDashboard__SectionHeader">
            <div>
              <Typography.Title level={4}>{t('admin_analytics_registration')}</Typography.Title>
              <Typography.Text type="secondary">
                {t('admin_analytics_registration_hint')}
              </Typography.Text>
            </div>
          </div>
          <div className="AnalyticsDashboard__RegistrationStats">
            <Statistic
              title={t('admin_analytics_registrations')}
              value={overview.registration.registrations}
            />
            <Statistic
              title={t('admin_analytics_confirmations')}
              value={overview.registration.confirmations}
            />
            <Statistic
              title={t('admin_analytics_confirmed_7d')}
              value={overview.registration.confirmedWithinSevenDaysPercent ?? '—'}
              suffix={
                overview.registration.confirmedWithinSevenDaysPercent == null ? undefined : '%'
              }
            />
            <Statistic
              title={t('admin_analytics_pending_7d')}
              value={overview.registration.pendingConfirmationOverSevenDays}
            />
          </div>
          {overview.registration.confirmationCohortIsForming && (
            <Typography.Text type="secondary">
              {t('admin_analytics_confirmation_cohort_forming')}
            </Typography.Text>
          )}
          <div className="AnalyticsDashboard__LargeChart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={overview.registration.points}
                margin={{ top: 12, right: 20, left: 0, bottom: 0 }}
              >
                <CartesianGrid stroke="rgba(98, 109, 128, 0.2)" vertical={false} />
                <XAxis dataKey="label" tickLine={false} />
                <YAxis allowDecimals={false} tickLine={false} />
                <ChartTooltip contentStyle={embedded ? CHART_TOOLTIP_V2 : CHART_TOOLTIP_LEGACY} />
                <Legend />
                <Line
                  name={t('admin_analytics_registrations')}
                  dataKey="registrations"
                  type="monotone"
                  stroke={embedded ? '#dcfa54' : '#35b2f6'}
                  strokeWidth={2}
                />
                <Line
                  name={t('admin_analytics_confirmations')}
                  dataKey="confirmations"
                  type="monotone"
                  stroke="#55c983"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      </>
    );
  };

  const renderRooms = () => {
    if (roomsLoading && !rooms) {
      return <Skeleton active paragraph={{ rows: 8 }} />;
    }
    if (!rooms) {
      return <Empty description={t('admin_analytics_empty')} />;
    }
    return (
      <>
        <Checkbox.Group
          className="AnalyticsDashboard__RoomToggles"
          value={selectedRooms}
          options={rooms.rooms.map((room) => ({
            label: pokerSitePostfix[room.pokersiteId],
            value: room.pokersiteId,
          }))}
          onChange={(values) => setSelectedRooms(values.map(Number) as PokerSiteId[])}
        />
        <div className="AnalyticsDashboard__LargeChart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={roomChartData} margin={{ top: 12, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(98, 109, 128, 0.2)" vertical={false} />
              <XAxis dataKey="label" tickLine={false} />
              <YAxis allowDecimals={false} tickLine={false} />
              <ChartTooltip contentStyle={embedded ? CHART_TOOLTIP_V2 : CHART_TOOLTIP_LEGACY} />
              <Legend />
              {rooms.rooms
                .filter((room) => selectedRooms.includes(room.pokersiteId))
                .map((room) => (
                  <Line
                    key={room.pokersiteId}
                    name={pokerSitePostfix[room.pokersiteId]}
                    dataKey={String(room.pokersiteId)}
                    type="monotone"
                    stroke={(embedded ? ROOM_COLORS_V2 : ROOM_COLORS)[room.pokersiteId]}
                    strokeWidth={2}
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <Table<AnalyticsRoomSeriesDto>
          rowKey="pokersiteId"
          pagination={false}
          dataSource={rooms.rooms}
          scroll={{ x: 560 }}
          columns={[
            {
              title: t('admin_analytics_room'),
              dataIndex: 'pokersiteId',
              render: (value: PokerSiteId) => pokerSitePostfix[value],
            },
            {
              title: t('admin_analytics_searches'),
              dataIndex: 'totalSearches',
              render: (value: number) => numberFormatter.format(value),
            },
            {
              title: t('admin_analytics_unique_users'),
              dataIndex: 'uniqueUsers',
              render: (value: number) => numberFormatter.format(value),
            },
            {
              title: t('admin_analytics_share'),
              dataIndex: 'sharePercent',
              render: (value: number) => `${numberFormatter.format(value)}%`,
            },
          ]}
        />
      </>
    );
  };

  return (
    <div className={`AnalyticsDashboard${embedded ? ' AnalyticsDashboard--embedded' : ''}`}>
      <header className="AnalyticsDashboard__PageHeader">
        <div>
          <Typography.Title level={2}>{t('admin_analytics_title')}</Typography.Title>
          <Typography.Text type="secondary">{t('admin_analytics_subtitle')}</Typography.Text>
        </div>
      </header>

      <section className="AnalyticsDashboard__Section">
        <div className="AnalyticsDashboard__SectionHeader">
          <Typography.Title level={4}>{t('admin_analytics_activity')}</Typography.Title>
          <UnitFilter
            value={overviewUnit}
            onChange={(unit) => dispatch(loadAnalyticsOverview(unit))}
          />
        </div>
        {overviewError && (
          <Alert
            type="error"
            showIcon
            message={overviewError}
            action={
              <Button size="small" onClick={() => dispatch(loadAnalyticsOverview(overviewUnit))}>
                {t('retry')}
              </Button>
            }
          />
        )}
        {renderOverview()}
      </section>

      <section className="SurfaceCard AnalyticsDashboard__Section">
        <div className="AnalyticsDashboard__SectionHeader">
          <div>
            <Typography.Title level={4}>{t('admin_analytics_rooms')}</Typography.Title>
            <Typography.Text type="secondary">{t('admin_analytics_rooms_hint')}</Typography.Text>
          </div>
          <UnitFilter value={roomsUnit} onChange={(unit) => dispatch(loadAnalyticsRooms(unit))} />
        </div>
        {roomsError && (
          <Alert
            type="error"
            showIcon
            message={roomsError}
            action={
              <Button size="small" onClick={() => dispatch(loadAnalyticsRooms(roomsUnit))}>
                {t('retry')}
              </Button>
            }
          />
        )}
        {renderRooms()}
        <Typography.Text type="secondary" className="AnalyticsDashboard__PartialNote">
          {t('admin_analytics_partial_note')}
        </Typography.Text>
      </section>
    </div>
  );
});

export default AdminHomePage;
